import { z } from 'zod';

import {
  InteractionType,
  OptionRenderType,
  ProjectType,
  QuestionType,
} from '@/manager-graphql';
import {
  FormQuestion,
  FrontendQuestionType,
  generateNewUUID,
  QuestionTypeWithFrontendTypes,
} from '@/shared-project-builder-v2-core';
import {
  getQuestionKindCapabilities,
  hasUrlOrTaskQuestion,
  isMobileAppQuestion,
  MOBILE_APP_QUESTION_TEXT,
  parseMessageText,
  plainTextToMessageText,
  resolveDefaultMaxSelections,
  resolvePersistableSelectionLimits,
  resolveQuestionKind,
  SELECTION_LIMIT_MAX,
} from '@/shared-question-model';
import {
  canPlayUrl,
  isValidUrlExtended,
  safeRandomUUID,
  toDisplayUrl,
} from '@/shared-utilities';

import { toQuestionKindInput } from './shared-manager-project-question-kind.adapter';
import {
  getUrlQuestionText,
  isUrlQuestionText,
  URL_QUESTION_TEXT,
} from './shared-manager-project-url-question-text.consts';
import { CONTACT_INFORMATION_TAG_NAME } from './shared-manager-project.consts';

export { getUrlQuestionText, isUrlQuestionText, URL_QUESTION_TEXT };

export function hasContactInfoTag(
  tags: ReadonlyArray<{ name?: string | null } | null> | null | undefined
): boolean {
  return !!tags?.some((tag) => tag?.name === CONTACT_INFORMATION_TAG_NAME);
}

export function resolveContactInfoTagsOnTypeChange<
  T extends { name?: string | null } | null,
>(
  tags: ReadonlyArray<T> | null | undefined,
  nextType: QuestionTypeWithFrontendTypes
): ReadonlyArray<T | { name: string }> | null | undefined {
  const isContactInfo = hasContactInfoTag(tags);

  if (nextType === FrontendQuestionType.NameEmail) {
    if (isContactInfo) return tags;
    return [...(tags ?? []), { name: CONTACT_INFORMATION_TAG_NAME }];
  }

  if (!isContactInfo) return tags;
  return tags?.filter((tag) => tag?.name !== CONTACT_INFORMATION_TAG_NAME);
}

function isPlainTextQuestionType(type: QuestionTypeWithFrontendTypes) {
  return (
    type !== QuestionType.Message &&
    type !== FrontendQuestionType.Url &&
    type !== FrontendQuestionType.MobileApp
  );
}

export function resolveQuestionTextOnTypeChange(
  text: string | null | undefined,
  previousType: QuestionTypeWithFrontendTypes,
  nextType: QuestionTypeWithFrontendTypes
): string | null {
  if (
    previousType === QuestionType.Message &&
    isPlainTextQuestionType(nextType)
  ) {
    return parseMessageText(text).title;
  }

  if (
    isPlainTextQuestionType(previousType) &&
    nextType === QuestionType.Message
  ) {
    return plainTextToMessageText(text);
  }

  return null;
}

export function getQuestionType(
  value: QuestionType,
  optionRenderType: OptionRenderType,
  text?: string,
  subQuestionsCount?: number,
  mobileAppEmbedUrl?: string | null
) {
  if (
    value === QuestionType.MultipleChoice &&
    optionRenderType === OptionRenderType.Media
  ) {
    return FrontendQuestionType.ImageChoice;
  }

  if (value === QuestionType.Rating && (subQuestionsCount ?? 0) > 0) {
    return FrontendQuestionType.MatrixRating;
  }

  if (value === QuestionType.Message && isUrlQuestionText(text)) {
    return FrontendQuestionType.Url;
  }

  if (
    value === QuestionType.Message &&
    isMobileAppQuestion({ text, mobileAppEmbedUrl })
  ) {
    return FrontendQuestionType.MobileApp;
  }

  return value;
}

/**
 * Inverse of `getQuestionType`: the `QuestionType` a frontend sentinel is
 * actually persisted as. `NameEmail` maps to `OpenEnded` because Contact Info
 * is an open-ended question distinguished only by its `internal:contact` tag
 * (see `REQUEST_NAME_EMAIL_PLACEHOLDER`), not by its own type.
 */
export function toBackendQuestionType(
  value: QuestionTypeWithFrontendTypes
): QuestionType {
  switch (value) {
    case FrontendQuestionType.ImageChoice:
      return QuestionType.MultipleChoice;
    case FrontendQuestionType.Url:
    case FrontendQuestionType.MobileApp:
      return QuestionType.Message;
    case FrontendQuestionType.MatrixRating:
      return QuestionType.Rating;
    case FrontendQuestionType.NameEmail:
      return QuestionType.OpenEnded;
    default:
      return value;
  }
}

export function validateMediaUrlSync(url: string): boolean {
  if (!url) return true;

  try {
    const _parsedUrl = new URL(
      url.toLowerCase().startsWith('http') ? url : `https://${url}`
    );
  } catch {
    return false;
  }

  if (canPlayUrl(url)) return true;

  return true;
}

const assetSchema = z.object({
  id: z.guid(),
  name: z.string().trim().nullish(),
  description: z.string().trim().nullish(),
  url: z.string().refine(
    (url) => {
      if (!url) return true;
      return validateMediaUrlSync(url);
    },
    {
      error:
        'Please enter an image or video URL. To embed a Figma prototype or other page, use a URL Redirect question.',
    }
  ),
  createdAt: z.any().nullish(),
  updatedAt: z.any().nullish(),
});

export const questionOptionSchema = z.object({
  id: z.guid().optional(),
  label: z.string().trim().min(1, { error: 'Option is required.' }),
  value: z.string().trim().min(1, { error: 'Value is required.' }),
  assets: z.array(assetSchema).nullish(),
});

const imageChoiceOptionSchema = z.object({
  id: z.guid().optional(),
  label: z.string().trim().min(1, { error: 'Image description is required' }),
  value: z.string().trim().min(1, { error: 'Value is required.' }),
  assets: z.array(assetSchema).nullish(),
});

const tagSchema = z.object({
  id: z.guid().optional(),
  color: z
    .string()
    .trim()
    .refine((value) => /^#([0-9A-F]{6}){1,2}$/i.test(value), {
      error: 'Color must be a valid hex color.',
    })
    .nullish(),
  name: z.string().trim().min(1, { error: 'Name is required.' }),
});

export function transformFigmaUrl(
  url: string | null | undefined
): string | null | undefined {
  if (!url) return url;

  try {
    const normalizedUrl = url.toLowerCase().startsWith('http')
      ? url
      : `https://${url}`;

    const urlObj = new URL(normalizedUrl);

    if (
      urlObj.hostname === 'figma.com' ||
      urlObj.hostname === 'www.figma.com'
    ) {
      urlObj.hostname = 'embed.figma.com';
    }

    if (urlObj.hostname === 'embed.figma.com') {
      urlObj.searchParams.set('embed-host', 'share');
      return urlObj.toString();
    }

    return urlObj.toString();
  } catch {
    return url;
  }
}

function isContentUrlWellFormed(url: string | null | undefined): boolean {
  return !url || isValidUrlExtended(url);
}

const CONTENT_URL_FORMAT_ERROR = {
  error: 'Invalid url. Please enter a valid url.',
};

const contentUrlFormatSchema = z
  .string()
  .trim()
  .nullish()
  .refine(isContentUrlWellFormed, CONTENT_URL_FORMAT_ERROR)
  .optional();

export const contentUrlSchema = z
  .string()
  .trim()
  .nullish()
  .transform(transformFigmaUrl)
  .refine(isContentUrlWellFormed, CONTENT_URL_FORMAT_ERROR)
  .optional();

const FORM_DECODED_CONTENT_URL_TYPES: ReadonlySet<
  QuestionType | null | undefined
> = new Set([QuestionType.Task, QuestionType.Message]);

export function toFormContentUrl(question: {
  type?: QuestionType | null;
  contentUrl?: string | null;
}): string | null | undefined {
  const { type, contentUrl } = question;

  if (!contentUrl || !FORM_DECODED_CONTENT_URL_TYPES.has(type)) {
    return contentUrl;
  }

  return toDisplayUrl(contentUrl);
}

export const baseQuestionSchema = z.object({
  id: z.guid(),
  aiInsights: z.boolean(),
  aiConvoDepth: z.number().int(),
  assets: z.array(assetSchema).nullish(),
  required: z.boolean(),
  maxSelections: z.number().nullish(),
  minSelections: z.number().nullish(),
  contentUrl: contentUrlFormatSchema,
  randomize: z.boolean().nullish(),
  successQuestionEnabled: z.boolean().nullish(),
  feedbackQuestionEnabled: z.boolean().nullish(),
  tags: z.array(tagSchema).nullish(),
  text: z.string().trim().min(1, { error: 'Question is required' }),
  optionRenderType: z.enum(OptionRenderType).catch(OptionRenderType.Text),
  interactionType: z.enum(InteractionType).nullish(),
  showLiveDistribution: z.boolean().nullish(),
});

export const multipleChoiceSchema = baseQuestionSchema.extend({
  type: z.literal(QuestionType.MultipleChoice),
  randomize: z.boolean(),
  maxSelections: z.number().nullish(),
  options: z.array(questionOptionSchema).min(2, {
    error: 'Multiple choice questions must have at least two options.',
  }),
});

export const imageChoiceSchema = baseQuestionSchema.extend({
  type: z.literal(QuestionType.MultipleChoice),
  randomize: z.boolean(),
  maxSelections: z.number().nullish(),
  options: z.array(imageChoiceOptionSchema).min(2, {
    error: 'Image choice questions must have at least two options.',
  }),
});

export const openEndedSchema = baseQuestionSchema.extend({
  type: z.literal(QuestionType.OpenEnded),
  options: z.array(questionOptionSchema).max(0, {
    error: 'Open-ended questions cannot have options.',
  }),
});

export const rankingSchema = baseQuestionSchema.extend({
  type: z.literal(QuestionType.Ranking),
  options: z
    .array(questionOptionSchema)
    .min(2, { error: 'Ranking questions must have at least two options.' }),
});

const ratingOptionSchema = z
  .object({
    id: z.guid().optional(),
    label: z.string().trim(),
    value: z.string().trim().min(1, { error: 'Value is required.' }),
    assets: z.array(assetSchema).nullish(),
  })
  .transform((option) => ({
    ...option,
    label: option.label || option.value,
  }));

const subQuestionSchema = z.object({
  id: z.string().optional(),
  text: z.string(),
  type: z.enum(QuestionType).optional(),
  questionOrder: z.number().optional(),
});

export const MATRIX_NA_LABEL_MAX_LENGTH = 32;

export const MATRIX_MIN_SCALE_OPTIONS = 2;

const RATING_MIN_SCALE_OPTIONS = 3;

export const ratingScaleSchema = baseQuestionSchema
  .extend({
    type: z.literal(QuestionType.Rating),
    options: z.array(ratingOptionSchema).min(MATRIX_MIN_SCALE_OPTIONS),
    subQuestions: z.array(subQuestionSchema).nullish(),
    notApplicableLabel: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    const isMatrix = (data.subQuestions?.length ?? 0) > 0;
    if (!isMatrix) {
      if (data.options.length < RATING_MIN_SCALE_OPTIONS) {
        ctx.addIssue({
          code: 'custom',
          path: ['options'],
          message: `Rating questions need at least ${RATING_MIN_SCALE_OPTIONS} options.`,
        });
      }
      return;
    }

    data.subQuestions?.forEach((sq, sqIdx) => {
      if (!sq.text || sq.text.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['subQuestions', sqIdx, 'text'],
          message: 'Sub-question text is required.',
        });
      }
    });

    if (
      data.notApplicableLabel !== null &&
      data.notApplicableLabel !== undefined
    ) {
      const trimmed = data.notApplicableLabel.trim();
      if (trimmed.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['notApplicableLabel'],
          message: 'N/A label cannot be empty.',
        });
      } else if (data.notApplicableLabel.length > MATRIX_NA_LABEL_MAX_LENGTH) {
        ctx.addIssue({
          code: 'custom',
          path: ['notApplicableLabel'],
          message: `N/A label must be ${MATRIX_NA_LABEL_MAX_LENGTH} characters or fewer.`,
        });
      }
    }
  });

export const messageSchema = baseQuestionSchema.extend({
  type: z.literal(QuestionType.Message),
  required: z.literal(false),
  aiConvoDepth: z.literal(0),
  options: z.array(questionOptionSchema).max(0, {
    error: 'Message questions cannot have options.',
  }),
});

export const taskSchema = baseQuestionSchema.extend({
  type: z.literal(QuestionType.Task),
  required: z.literal(false),
  aiConvoDepth: z.literal(0),
  contentUrl: contentUrlSchema,
  options: z.array(questionOptionSchema).max(0, {
    error: 'Task questions cannot have options.',
  }),
  successQuestionEnabled: z.boolean().nullish(),
  feedbackQuestionEnabled: z.boolean().nullish(),
});

const messageOrUrlOrMobileAppShape = baseQuestionSchema.extend({
  type: z.literal(QuestionType.Message),
  required: z.literal(false),
  aiConvoDepth: z.literal(0),
  contentUrl: contentUrlSchema,
  options: z.array(questionOptionSchema).max(0, {
    error: 'Message questions cannot have options.',
  }),
  mobileAppEmbedUrl: z.string().trim().nullish(),
  mobileAppFileName: z.string().trim().nullish(),
});

type MessageOrUrlOrMobileAppInput = z.output<
  typeof messageOrUrlOrMobileAppShape
>;

function refineUrlQuestion(
  data: MessageOrUrlOrMobileAppInput,
  ctx: z.RefinementCtx
) {
  if (!isUrlQuestionText(data.text)) return;
  if (data.contentUrl) return;
  ctx.addIssue({
    code: 'custom',
    path: ['contentUrl'],
    message: 'URL is required',
  });
}

export const messageOrUrlOrMobileAppSchema =
  messageOrUrlOrMobileAppShape.superRefine((data, ctx) => {
    if (isMobileAppQuestion(data)) {
      const embedUrl = data.mobileAppEmbedUrl?.trim() ?? '';
      if (!embedUrl) {
        ctx.addIssue({
          code: 'custom',
          path: ['mobileAppEmbedUrl'],
          message: 'Mobile app upload is required',
        });
      }
      return;
    }
    refineUrlQuestion(data, ctx);
  });

const persistableMessageOrUrlOrMobileAppSchema =
  messageOrUrlOrMobileAppShape.superRefine((data, ctx) => {
    if (isMobileAppQuestion(data)) return;
    refineUrlQuestion(data, ctx);
  });

const questionSchema = z.union([
  multipleChoiceSchema,
  openEndedSchema,
  rankingSchema,
  ratingScaleSchema,
  taskSchema,
  messageOrUrlOrMobileAppSchema,
]);

const persistableQuestionSchema = z.union([
  multipleChoiceSchema,
  openEndedSchema,
  rankingSchema,
  ratingScaleSchema,
  taskSchema,
  persistableMessageOrUrlOrMobileAppSchema,
]);

export const questionsSchema = z.array(questionSchema).min(1, {
  error: 'Project must contain at least one question.',
});

export const persistableQuestionsSchema = z
  .array(persistableQuestionSchema)
  .min(1, {
    error: 'Project must contain at least one question.',
  });

export const questionValidationMap = {
  [QuestionType.MultipleChoice]: multipleChoiceSchema,
  [QuestionType.OpenEnded]: openEndedSchema,
  [QuestionType.Ranking]: rankingSchema,
  [QuestionType.Rating]: ratingScaleSchema,
  [QuestionType.Message]: messageSchema,
  [QuestionType.Task]: taskSchema,
  [FrontendQuestionType.Url]: messageOrUrlOrMobileAppSchema,
  [FrontendQuestionType.MobileApp]: messageOrUrlOrMobileAppSchema,
  [FrontendQuestionType.ImageChoice]: imageChoiceSchema,
};

/**
 * Picks the schema a question is judged by and runs it. Split out of
 * `useIsQuestionValid` so Builder V2 can ask the same question about a plain
 * value — the two builders must never disagree on what "incomplete" means.
 */
export function resolveQuestionValidation(question: FormQuestion | undefined) {
  if (!question || !question.type) {
    return {
      isValid: false,
      error: { issues: [{ message: 'Question is required' }] },
    };
  }

  let validation = questionValidationMap[question.type].safeParse(question);

  if (
    question.type === QuestionType.MultipleChoice &&
    question.optionRenderType === OptionRenderType.Media
  ) {
    validation =
      questionValidationMap[FrontendQuestionType.ImageChoice].safeParse(
        question
      );
  }

  if (
    question.type === QuestionType.Message &&
    isUrlQuestionText(question.text)
  ) {
    validation =
      questionValidationMap[FrontendQuestionType.Url].safeParse(question);
  }

  if (question.type === QuestionType.Message && isMobileAppQuestion(question)) {
    validation =
      questionValidationMap[FrontendQuestionType.MobileApp].safeParse(question);
  }

  return {
    isValid: validation.success,
    error: validation.success ? undefined : validation.error,
  };
}

export type RatingConfigurableQuestion = Pick<
  FormQuestion,
  'type' | 'options' | 'subQuestions'
>;

export interface RatingEndpointGaps {
  readonly low: boolean;
  readonly high: boolean;
}

const NO_RATING_ENDPOINT_GAPS: RatingEndpointGaps = { low: false, high: false };

/**
 * Which end of a rating scale is missing its label. Both plain Rating Scale and
 * Matrix Rating Scale validate their scale endpoint labels (first and last
 * option).
 *
 * Lives outside the zod schemas because `ratingOptionSchema` backfills an empty
 * label from the option's value, so a field-level rule there can never fire.
 * Split per end so the builder can mark the offending input rather than the
 * whole question; `isUnconfiguredRatingQuestion` reads the same result, so the
 * publish gate and the red state can never disagree.
 */
export function resolveRatingEndpointGaps(
  question: RatingConfigurableQuestion
): RatingEndpointGaps {
  if (question.type !== QuestionType.Rating) return NO_RATING_ENDPOINT_GAPS;

  const options = question.options ?? [];
  if (options.length === 0) return NO_RATING_ENDPOINT_GAPS;

  return {
    low: !options[0]?.label?.trim(),
    high: !options[options.length - 1]?.label?.trim(),
  };
}

export function isUnconfiguredRatingQuestion(
  question: RatingConfigurableQuestion
): boolean {
  const gaps = resolveRatingEndpointGaps(question);
  return gaps.low || gaps.high;
}

export function resolveClassifiableQuestions(
  questions: readonly FormQuestion[] | null | undefined
): FormQuestion[] {
  return (questions ?? []).filter(
    (question) =>
      resolveQuestionValidation(question).isValid &&
      !isUnconfiguredRatingQuestion(question)
  );
}

export const isValidUrl = (
  url: string,
  supportedPlatforms: Array<{ keyword: string | string[] }>
) => {
  if (url.trim() === '') return false;

  try {
    const _parsedUrl = new URL(`https://${url}`);

    return supportedPlatforms.some((platform) => {
      const keywords = Array.isArray(platform.keyword)
        ? platform.keyword
        : [platform.keyword];
      return keywords.some((keyword) =>
        url.toLowerCase().includes(keyword.toLowerCase())
      );
    });
  } catch {
    return false;
  }
};

const QUESTION_TYPE_VALUES = new Set<string>(Object.values(QuestionType));

export const isQuestionType = (value: string): value is QuestionType => {
  return QUESTION_TYPE_VALUES.has(value);
};

export const toQuestionType = (
  value: string | null | undefined
): QuestionType | null => {
  if (typeof value !== 'string') return null;
  return isQuestionType(value) ? value : null;
};

export const isOtherOption = (option: { label: string; value: string }) => {
  return option.value?.trim().toLowerCase() === 'other';
};

export function ensureOptionValue<
  T extends {
    label?: string;
    value?: string | null;
  },
>(option: T, index: number): T & { value: string } {
  const valueTrim = option.value?.trim();
  if (valueTrim) return { ...option, value: valueTrim };
  const labelTrim = option.label?.trim() ?? '';
  const value = labelTrim.toLowerCase() === 'other' ? 'other' : `opt-${index}`;
  return { ...option, value };
}

export const RATING_QUESTION_PLACEHOLDER: z.infer<typeof ratingScaleSchema> = {
  id: generateNewUUID(),
  type: QuestionType.Rating,
  text: '',
  required: true,
  aiInsights: true,
  aiConvoDepth: 2,
  assets: [],
  optionRenderType: OptionRenderType.Text,
  options: [
    { label: '', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '', value: '5' },
  ],
};

export const MATRIX_QUESTION_PLACEHOLDER: z.infer<typeof ratingScaleSchema> = {
  ...RATING_QUESTION_PLACEHOLDER,
  id: generateNewUUID(),
  aiInsights: false,
  aiConvoDepth: 0,
  subQuestions: [{ text: '', type: QuestionType.Rating, questionOrder: 0 }],
  notApplicableLabel: null,
};

export const OPEN_ENDED_QUESTION_PLACEHOLDER: z.infer<typeof openEndedSchema> =
  {
    ...RATING_QUESTION_PLACEHOLDER,
    type: QuestionType.OpenEnded,
    options: [],
  };

export const RANKING_QUESTION_PLACEHOLDER: z.infer<typeof rankingSchema> = {
  ...RATING_QUESTION_PLACEHOLDER,
  type: QuestionType.Ranking,
  options: [{ label: '', value: safeRandomUUID() }],
};

export const MULTIPLE_CHOICE_QUESTION_PLACEHOLDER: z.infer<
  typeof multipleChoiceSchema
> = {
  ...RANKING_QUESTION_PLACEHOLDER,
  type: QuestionType.MultipleChoice,
  randomize: false,
  maxSelections: resolveDefaultMaxSelections(
    RANKING_QUESTION_PLACEHOLDER.options.length
  ),
};

export const IMAGE_CHOICE_QUESTION_PLACEHOLDER: z.infer<
  typeof imageChoiceSchema
> = {
  ...MULTIPLE_CHOICE_QUESTION_PLACEHOLDER,
  type: QuestionType.MultipleChoice,
  optionRenderType: OptionRenderType.Media,
  options: [],
  maxSelections: resolveDefaultMaxSelections(0),
};

export const MESSAGE_QUESTION_PLACEHOLDER: z.infer<typeof messageSchema> = {
  ...RATING_QUESTION_PLACEHOLDER,
  type: QuestionType.Message,
  aiConvoDepth: 0,
  required: false,
  options: [],
};

export const URL_QUESTION_PLACEHOLDER: z.infer<
  typeof messageOrUrlOrMobileAppSchema
> = {
  ...MESSAGE_QUESTION_PLACEHOLDER,
  type: QuestionType.Message,
  text: URL_QUESTION_TEXT,
  contentUrl: '',
};

export const MOBILE_APP_QUESTION_PLACEHOLDER: z.infer<
  typeof messageOrUrlOrMobileAppSchema
> = {
  ...MESSAGE_QUESTION_PLACEHOLDER,
  type: QuestionType.Message,
  text: MOBILE_APP_QUESTION_TEXT,
  mobileAppEmbedUrl: '',
  mobileAppFileName: '',
};

export const TASK_QUESTION_PLACEHOLDER: z.infer<typeof taskSchema> = {
  ...RATING_QUESTION_PLACEHOLDER,
  type: QuestionType.Task,
  aiConvoDepth: 0,
  required: false,
  options: [],
  successQuestionEnabled: true,
  feedbackQuestionEnabled: true,
};

export const REQUEST_NAME_EMAIL_PLACEHOLDER: z.infer<typeof openEndedSchema> = {
  ...OPEN_ENDED_QUESTION_PLACEHOLDER,
  text: 'What is your name and email address?',
  tags: [{ name: CONTACT_INFORMATION_TAG_NAME }],
  aiConvoDepth: 0,
};

export const OPTIONS_PLACEHOLDER: Record<
  QuestionTypeWithFrontendTypes,
  z.infer<typeof questionsSchema>[0]['options']
> = {
  [QuestionType.MultipleChoice]: MULTIPLE_CHOICE_QUESTION_PLACEHOLDER.options,
  [QuestionType.OpenEnded]: [],
  [QuestionType.Ranking]: RANKING_QUESTION_PLACEHOLDER.options,
  [QuestionType.Rating]: RATING_QUESTION_PLACEHOLDER.options,
  [FrontendQuestionType.ImageChoice]: [],
  [QuestionType.Message]: [],
  [FrontendQuestionType.Url]: [],
  [FrontendQuestionType.MobileApp]: [],
  [QuestionType.Task]: [],
  [FrontendQuestionType.NameEmail]: [],
  [FrontendQuestionType.MatrixRating]: RATING_QUESTION_PLACEHOLDER.options,
};

export interface SelectionLimits {
  minSelections: number | null;
  maxSelections: number | null;
}

export function hasSelectionLimit(
  questionType: QuestionTypeWithFrontendTypes
): boolean {
  return (
    questionType === QuestionType.MultipleChoice ||
    questionType === FrontendQuestionType.ImageChoice
  );
}

export function resolveSelectionLimitsOnTypeChange(
  questionType: QuestionTypeWithFrontendTypes,
  optionCount: number
): SelectionLimits {
  return {
    minSelections: null,
    maxSelections: hasSelectionLimit(questionType)
      ? resolveDefaultMaxSelections(optionCount)
      : null,
  };
}

export function resolveUpToSelectionLimits(count: number): SelectionLimits {
  return {
    minSelections: null,
    maxSelections: resolveDefaultMaxSelections(count),
  };
}

export function repairUpToSelectionLimits(
  minSelections: number | null | undefined,
  maxSelections: number | null | undefined,
  optionCount: number
): SelectionLimits | null {
  if (optionCount <= 0) return null;

  const withinOptions =
    maxSelections != null &&
    maxSelections > 0 &&
    maxSelections <= optionCount &&
    maxSelections <= SELECTION_LIMIT_MAX;

  if (withinOptions && minSelections == null) return null;

  return resolveUpToSelectionLimits(
    withinOptions ? maxSelections : optionCount
  );
}

export function withPersistableSelectionLimits<
  T extends {
    type?: QuestionType | null;
    minSelections?: number | null;
    maxSelections?: number | null;
    options?: readonly unknown[] | null;
  },
>(question: T): T {
  const limits = resolvePersistableSelectionLimits(
    question.minSelections,
    question.maxSelections,
    question.type === QuestionType.MultipleChoice,
    question.options?.length
  );

  return {
    ...question,
    minSelections: limits.minSelections,
    maxSelections: limits.maxSelections,
  };
}

export const OPTIONS: Array<[QuestionTypeWithFrontendTypes, string]> = [
  [QuestionType.MultipleChoice, 'Multiple choice'],
  [QuestionType.OpenEnded, 'Open-ended'],
  [QuestionType.Ranking, 'Ranking'],
  [QuestionType.Rating, 'Rating'],
  [FrontendQuestionType.ImageChoice, 'Image choice'],
  [FrontendQuestionType.NameEmail, 'Name & Email'],
];

export const FORM_OPTIONS: Array<[QuestionTypeWithFrontendTypes, string]> = [
  ...OPTIONS,
  [FrontendQuestionType.MatrixRating, 'Matrix rating scale'],
];

export const getQuestionTypeLabel = (type: QuestionTypeWithFrontendTypes) => {
  switch (type) {
    case QuestionType.MultipleChoice:
      return 'Multiple choice';
    case QuestionType.OpenEnded:
      return 'Open ended';
    case QuestionType.Rating:
      return 'Rating scale';
    case QuestionType.Ranking:
      return 'Ranking';
    case QuestionType.Task:
      return 'Task';
    case QuestionType.Message:
      return 'Message';
    case FrontendQuestionType.Url:
      return 'URL';
    case FrontendQuestionType.MobileApp:
      return 'Mobile App';
    case FrontendQuestionType.ImageChoice:
      return 'Image choice';
    case FrontendQuestionType.MatrixRating:
      return 'Matrix rating scale';
    default:
      return type;
  }
};

export const isMatrixQuestion = (
  question: Pick<FormQuestion, 'type' | 'subQuestions'> | null | undefined
): boolean => {
  if (!question) return false;
  return (
    question.type === QuestionType.Rating &&
    (question.subQuestions?.length ?? 0) > 0
  );
};

/**
 * URL and mobile-app questions are `Message` questions whose `text` is the
 * marker that identifies them, not editorial copy — overwriting it turns them
 * back into a plain message. Nothing may replace their text or type.
 */
export const isFixedCopyQuestion = (
  question:
    | Pick<FormQuestion, 'type' | 'text' | 'mobileAppEmbedUrl'>
    | null
    | undefined
): boolean => {
  if (question?.type !== QuestionType.Message) return false;
  return isUrlQuestionText(question.text) || isMobileAppQuestion(question);
};

export const isMatrixFrontendType = (
  type: QuestionTypeWithFrontendTypes
): boolean => type === FrontendQuestionType.MatrixRating;

export const isCountedProjectTypeQuestion = (question: {
  type: QuestionType;
  tags?: ReadonlyArray<{ name?: string | null } | null> | null;
}): boolean =>
  question.type !== QuestionType.Message && !hasContactInfoTag(question.tags);

export interface PollQuestionInput {
  readonly type: QuestionType;
  readonly optionRenderType?: OptionRenderType | null;
  readonly subQuestions?: readonly unknown[] | null;
  readonly tags?: readonly { name?: string | null }[] | null;
  readonly text?: string | null;
  readonly mobileAppEmbedUrl?: string | null;
}

export interface LiveResultsPollQuestionInput extends PollQuestionInput {
  readonly showLiveDistribution?: boolean | null;
}

function resolvePollQuestion<Question extends PollQuestionInput>(
  questions: readonly Question[] | null | undefined
): Question | null {
  const counted = questions?.filter(isCountedProjectTypeQuestion);
  if (counted?.length !== 1) return null;

  const [question] = counted;
  const kind = resolveQuestionKind(
    toQuestionKindInput({
      type: question.type,
      optionRenderType: question.optionRenderType,
      subQuestionsCount: question.subQuestions?.length,
      isContactInfo: false,
      isUrl: isUrlQuestionText(question.text),
      isMobileApp: isMobileAppQuestion(question),
    })
  );

  return getQuestionKindCapabilities(kind).supportsLiveDistribution
    ? question
    : null;
}

export function resolveIsPollProject(
  questions: readonly PollQuestionInput[] | null | undefined
): boolean {
  return resolvePollQuestion(questions) !== null;
}

export function isLiveResultsPoll(
  questions: readonly LiveResultsPollQuestionInput[] | null | undefined
): boolean {
  return !!resolvePollQuestion(questions)?.showLiveDistribution;
}

export function resolveLiveDistributionClears(
  questions: readonly LiveResultsPollQuestionInput[] | null | undefined
): number[] {
  if (!questions?.length || resolveIsPollProject(questions)) return [];

  return questions.reduce<number[]>((indexes, question, index) => {
    if (question.showLiveDistribution) indexes.push(index);
    return indexes;
  }, []);
}

export interface ScreenRecordingProjectQuestion {
  readonly type: QuestionType;
  readonly text?: string | null;
  readonly contentUrl?: string | null;
  readonly mobileAppEmbedUrl?: string | null;
  readonly optionRenderType?: OptionRenderType | null;
  readonly subQuestions?: readonly unknown[] | null;
  readonly tags?: readonly { name?: string | null }[] | null;
}

export function resolveRecordsScreenFromQuestions(
  questions: readonly ScreenRecordingProjectQuestion[] | null | undefined
): boolean {
  return hasUrlOrTaskQuestion(
    (questions ?? []).map((question) => ({
      kind: resolveQuestionKind(
        toQuestionKindInput({
          type: question.type,
          optionRenderType: question.optionRenderType,
          subQuestionsCount: question.subQuestions?.length,
          isContactInfo: hasContactInfoTag(question.tags),
          isUrl: isUrlQuestionText(question.text),
          isMobileApp: isMobileAppQuestion(question),
        })
      ),
      contentUrl: question.contentUrl,
      text: question.text,
    }))
  );
}

/**
 * Whether the responder arms screen capture for this project, which is what a
 * clip, a reel and an answer time all depend on. A legacy project answers from
 * its `ProjectType`; a project authored in the redesigned builder persists no
 * type at all, so the questions are the only signal — and they are the same
 * signal `selectShouldRecordScreen` reads at answer time.
 *
 * Widening is limited to the typeless case on purpose: a legacy Survey never
 * records, whatever its questions look like.
 */
export function resolveProjectRecordsScreen(
  project:
    | {
        type?: ProjectType | null;
        questions?: readonly ScreenRecordingProjectQuestion[] | null;
      }
    | null
    | undefined
): boolean {
  if (project?.type === ProjectType.UsabilityTesting) return true;
  if (project?.type !== null) return false;

  return resolveRecordsScreenFromQuestions(project.questions);
}
